'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import useSWRInfinite from 'swr/infinite';

import {
  DonationsListResponse,
  GetDonationsRequest,
  GetDonationsSortEnum,
  GetDonationsStatusesEnum,
  GetDonationsWithVisibleCommentEnum,
  ProjectDonationsListResponse,
  ProjectSettingsModeGroupEnum,
} from '@/app/api';
import { PropsWithClassname } from '@/app/types';
import { useToast } from '@/app/hooks/useToast';
import { donationsApiForClient } from '@/app/utils/apiForClient';

import Button, { ButtonHTMLTag } from '@/app/components/Button/Button';
import DonationList from '@/app/components/DonationList/DonationList';
import Loader from '@/app/components/Loader/Loader';
import SubprojectBanner from '@/app/components/SubprojectBanner/SubprojectBanner';
import { TabButton } from '@/app/components/ProjectDonorsTab/TabButton';
import { useProjectData } from '@/app/providers/ProjectProvider';

interface ProjectDonorsTabProps extends PropsWithClassname {
  projectId: string;
  projectNumericId: number;
  isSubprojectBannerVisible?: boolean;
}

enum DonationsFilter {
  Newest = 'newest',
  Highest = 'highest',
  WithComment = 'withComment',
}

const PAGE_SIZE = 10;

const HIGHEST_DONATIONS_PAGE_SIZE = 24;

const ProjectDonorsTab = ({
  projectId,
  projectNumericId,
  isSubprojectBannerVisible,
  className,
}: ProjectDonorsTabProps) => {
  const { project } = useProjectData();
  const isPlus = project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus;

  const { t } = useTranslation();
  const { showGenericErrorToast } = useToast();
  const [filterType, setFilterType] = useState<DonationsFilter>(DonationsFilter.Newest);

  const titleTranslations: Record<DonationsFilter, string> = {
    [DonationsFilter.Newest]: t('components.ProjectDonorsTab.filters.newest'),
    [DonationsFilter.Highest]: isPlus
      ? t('components.ProjectDonorsTab.filters.highestContributions')
      : t('components.ProjectDonorsTab.filters.highestDonations'),
    [DonationsFilter.WithComment]: t('components.ProjectDonorsTab.filters.withComment'),
  };

  const getKey = (pageIndex: number, previousPageData: ProjectDonationsListResponse | null) => {
    if (previousPageData && !previousPageData.donations.length) {
      return null;
    }

    const filter: GetDonationsRequest = {
      project: projectId,
      statuses: [GetDonationsStatusesEnum.Paid],
      limit: filterType === DonationsFilter.Highest ? HIGHEST_DONATIONS_PAGE_SIZE : PAGE_SIZE,
      offset: pageIndex * PAGE_SIZE,
      withVisibleComment:
        filterType === DonationsFilter.WithComment ? GetDonationsWithVisibleCommentEnum.NUMBER_1 : undefined,
      sort:
        filterType === DonationsFilter.Highest ? GetDonationsSortEnum.AmountDesc : GetDonationsSortEnum.CreatedAtDesc,
    };

    return JSON.stringify(filter);
  };

  const fetcher = async (key: string) => {
    const filter = JSON.parse(key) as GetDonationsRequest;
    return donationsApiForClient.getDonations(filter);
  };

  const { data, error, size, setSize, isValidating } = useSWRInfinite<DonationsListResponse>(getKey, fetcher, {
    revalidateFirstPage: false,
    onError: showGenericErrorToast,
  });

  const totoalItemsCount = data?.[0].totalItemCount || 0;

  const donations = data?.flatMap((page) => page.donations) || [];
  const isLoading = !data && !error;
  const isLoadMoreAvailable = totoalItemsCount > size * PAGE_SIZE && filterType !== DonationsFilter.Highest;

  const handleFilterChange = (filter: DonationsFilter) => {
    setFilterType(filter);
    setSize(1); // reset to first page on filter change
  };

  return (
    <div className={classNames(className, 'flex flex-col items-center')}>
      <div className="w-max lg:w-full overflow-x-auto flex gap-[8px] justify-center lg:justify-start pb-[8px] mb-[32px]">
        {Object.values(DonationsFilter).map((filter) => (
          <TabButton
            onClick={() => handleFilterChange(filter)}
            isActive={filterType === filter}
            key={filter}
            title={titleTranslations[filter]}
          />
        ))}
      </div>

      {!donations.length && !isLoading && (
        <div>
          <div className="my-[20px] mb-[140px]">{t('components.ProjectDonorsTab.noDonationsYet')}</div>
          {isSubprojectBannerVisible && <SubprojectBanner projectNumericId={projectNumericId} className="mt-[40px]" />}
        </div>
      )}

      {!!donations.length && <DonationList className="w-full" donations={donations} />}
      {isValidating && <Loader />}

      {isLoadMoreAvailable && (
        <Button
          testId="load-more-donations-button"
          className={'self-center px-[24px] py-[20px]'}
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.BUTTON,
            onClick: () => setSize(size + 1),
          }}
        >
          {t('components.ProjectDonorsTab.moreDonations')}
        </Button>
      )}

      {isSubprojectBannerVisible && <SubprojectBanner projectNumericId={projectNumericId} className="mt-[40px]" />}
    </div>
  );
};

export default ProjectDonorsTab;
